/**
 * ریشه‌ی سرور، و چرا هر نشانی‌ای پذیرفته نمی‌شود.
 *
 * هر سه کلاینت (بازی، پنلِ قدیم، پنلِ تازه) `?server=` را می‌پذیرفتند، هر
 * چه بود در `localStorage` نگه می‌داشتند، و از آن به بعد **توکن را به همان
 * نشانی می‌فرستادند**. یعنی یک لینک — روی دامنه‌ی خودمان، که هیچ چیزش
 * مشکوک نیست — کافی بود:
 *
 *     https://<سرورِ ما>/panel/players?server=https://evil.tld
 *
 * مرورگر برای هدرِ Authorization اول preflight می‌زند؛ سرورِ مهاجم CORS را
 * باز می‌گذارد، درخواستِ اصلی با توکنِ ادمین می‌رود، و تمام. بدتر: چون
 * نشانی ذخیره می‌شد، **ورودِ بعدی** هم نام و رمز و کدِ دومرحله‌ای را به
 * همان‌جا می‌برد. دومرحله‌ای این‌جا هیچ کمکی نمی‌کند.
 *
 * و چون هر سه کلاینت یک کلید (`bazivan.server`) داشتند، آلوده‌کردن از
 * `/play` پنل را هم می‌برد. پس هر سه از همین یک تابع می‌خوانند؛ درستش‌کردن
 * در یکی و نه بقیه، یعنی درست نشده.
 *
 * ## قاعده
 *
 * دو شرط، و هر دو باید برقرار باشد:
 *
 *  ۱. **صفحه‌ی جاری خودش محلی باشد.** روی دامنه‌ی واقعی هیچ جای‌گزینی
 *     پذیرفته نمی‌شود — آن‌جا سرور همان جایی است که صفحه از آن آمده.
 *  ۲. **مقصد هم محلی باشد**: لوپ‌بک، شبکه‌ی خصوصی، یا `.local`.
 *
 * این دو، کارِ روزمره را نمی‌بندد (`?server=http://192.168.1.4:8842` از
 * گوشی روی همان وای‌فای هنوز کار می‌کند، چون صفحه هم از همان‌جا آمده) ولی
 * نشانیِ اینترنتی را قطعاً رد می‌کند.
 *
 * مقدارِ ردشده **پاک هم می‌شود**: نگه‌داشتنش یعنی یک بار کلیک، همیشه آلوده.
 */

const KEY = "bazivan.server";

/** میزبانِ محلی؟ لوپ‌بک، شبکه‌ی خصوصی (RFC 1918)، یا mDNS. */
export function localHost(host: string): boolean {
  const h = host.toLowerCase().replace(/^\[|\]$/g, "");
  if (h === "localhost" || h === "::1" || h.endsWith(".local") || h.endsWith(".localhost")) return true;
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(h);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if (m.slice(1).some((x) => Number(x) > 255)) return false;
  return a === 127 || a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
}

/**
 * نشانیِ جای‌گزین پذیرفتنی است؟
 *
 * `here` مبدأِ صفحه است. نسبی‌بودن هم حساب می‌شود: `new URL` با پایه‌ی
 * `here` نشانیِ `//evil.tld` را هم درست باز می‌کند، که همان چیزی است که
 * یک سنجشِ سرسری از قلم می‌اندازد.
 */
export function allowedServer(candidate: string, here: string): boolean {
  let page: URL, want: URL;
  try {
    page = new URL(here);
    want = new URL(candidate, here);
  } catch {
    return false;
  }
  if (want.protocol !== "http:" && want.protocol !== "https:") return false;
  if (want.origin === page.origin) return true;
  return localHost(page.hostname) && localHost(want.hostname);
}

/**
 * ریشه‌ی سرور برای این صفحه. خالی = هم‌ریشه.
 *
 * `read`/`write`/`clear` تزریق می‌شوند تا تابع بی‌مرورگر هم آزمودنی باشد.
 */
export function pickServer(
  search: string,
  here: string,
  store: { read(): string | null; write(v: string): void; clear(): void },
): string {
  const q = new URLSearchParams(search).get("server");
  if (q) {
    if (!allowedServer(q, here)) {
      store.clear();
      return "";
    }
    store.write(q);
    return q.replace(/\/$/, "");
  }
  const saved = store.read();
  if (!saved) return "";
  // ذخیره‌ی قدیمی هم از نو سنجیده می‌شود: شاید پیش از این وصله نشسته باشد.
  if (!allowedServer(saved, here)) {
    store.clear();
    return "";
  }
  return saved.replace(/\/$/, "");
}

/** همان، با `localStorage` و `location` ِ واقعی. بی‌DOM: خالی. */
export function serverOrigin(): string {
  if (typeof location === "undefined") return "";
  return pickServer(location.search, location.href, {
    read() { try { return localStorage.getItem(KEY); } catch { return null; } },
    write(v) { try { localStorage.setItem(KEY, v); } catch { /* ناشناس */ } },
    clear() { try { localStorage.removeItem(KEY); } catch { /* ناشناس */ } },
  });
}
